import { Box, ClickAwayListener, Divider, MenuItem, Popper, TextField } from "@mui/material";
import Paper from "@mui/material/Paper";
import { MprcChip } from "components";
import { advFilterAddCommand, GenericListItemDto } from "components/MprcAdvFilter";
import { AutoCompleteLegacyDataModel, RenderSuggestion } from "components/MprcAdvFilter/RenderSuggestion";
import { MprcRequestErrorReport } from "components/MprcRequestErrorReport";
import { MprcSpinnerIndicator } from "components/MprcSpinnerIndicator";
import { InputColorClasses, InputColorClassKeys } from "components/styles/InputColorClasses";
import Downshift, { ControllerStateAndHelpers, StateChangeOptions } from "downshift";
import { SearchTextDto, SearchTextRule } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import LibUtils from "lib/LibUtils";
import * as React from "react";
import { Fragment, useEffect, useImperativeHandle, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { useMprcTheme } from "../MprcThemeProvider";
import { DatePickerDialog } from "./DatePickerDialog";
import { AdvFilterCommand, AdvFilterPicker, AdvFilterResult, AutoCompleteKeyWord, CustomPickerResult, MprcAdvFilterHandle } from "./interfaces";
import { AdvFilterStyles } from "./styles";
import TextPickerDrawer from "./TextPickerDrawer";

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    id: string;
    container?: any;
    disabled?: boolean;
    label?: string;
    title?: string;
    placeholderFocus?: string;
    placeholder?: string;
    autoFocus?: boolean;
    overModal?: boolean;
    underlineColor?: MprcColorsExtended;
    inputValue?: string;
    commands: AdvFilterCommand[] | null;
    selectedCommands: AdvFilterResult[];

    onEscape?: () => void;
    onUpdateSelection: (inputValue: string, commands: AdvFilterResult[], pressedEnter: boolean) => void;

    autocomplete?: {
        tokenizeSelection: boolean;
        maxSuggestionCount?: number | null;
        onGetSuggestions: (searchText: string, offset: number, take: number) => Promise<AutoCompleteLegacyDataModel[]>;
    };
}

interface IStateCommand {
    loadingCommands: boolean;
    commandItems: AdvFilterCommand[];
}

interface IStateAutoComplete {
    loading: boolean;
    lastError: any;
    suggestionLoadMore: boolean;
    searchText: string;
    searchSkipRecords: number;
    autoCompleteItems: Array<AutoCompleteLegacyDataModel>;
}

interface IState {
    inputValue: string;
    inputCommand: string;
    commandOpened: boolean;
    currentCommand: AdvFilterCommand | null;
    autoCompletedOpened: boolean;
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const LoadMoreText: React.FC<{ count: number; }> = ({ count }) => {
    const { theme } = useMprcTheme();
    return <span>{`${theme.MprcAdvFilter.loadMoreNum} ${count}`}</span>;
};

const EmptyRecordSet: React.FC<{}> = () => {
    const { theme } = useMprcTheme();
    return (
        <Box padding={1}>
            <b>{theme.MprcAdvFilter.emptyRecordset}</b>
        </Box>
    );
};

const useStyles = makeStyles<void, InputColorClassKeys>()(
    (theme, p, classes) => ({
        ...InputColorClasses(theme)
    })
);

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const MprcAdvFilterComponent: React.ForwardRefRenderFunction<MprcAdvFilterHandle, IProps> = (props, forwardRef) => {
    var escapeCount: number = 0;
    var inputRef: any;
    var ref: any;

    const popperRef = useRef<HTMLDivElement>(null);
    const { classes, cx } = AdvFilterStyles();
    const { classes: colorClasses } = useStyles();
    const { theme } = useMprcTheme();

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    useImperativeHandle(forwardRef, () => ({
        addCommand,
        closeFilter,
        resetFilter
    }));

    const addCommand = (command: string, displayText: string, value: any) => {
        var selectedItems = advFilterAddCommand(props.commands, props.selectedCommands, command, value, displayText);
        updateSelection(state.inputValue, selectedItems);
    };

    const closeFilter = () => {
        setState({ ...state, commandOpened: false, autoCompletedOpened: false });
    };

    const resetFilter = () => {
        setState({ ...state, inputValue: "", commandOpened: false, autoCompletedOpened: false });
        updateSelection("", []);
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    const [state, setState] = useState<IState>({
        inputValue: props.inputValue || "",
        inputCommand: "",
        commandOpened: false,
        autoCompletedOpened: false,
        currentCommand: null
    });

    const [stateAutoComplete, setStateAutoComplete] = useState<IStateAutoComplete>({
        autoCompleteItems: [],
        loading: false,
        searchText: "",
        searchSkipRecords: 0,
        suggestionLoadMore: false,
        lastError: null
    });

    const [stateCmd, setStateCmd] = useState<IStateCommand>({
        commandItems: [],
        loadingCommands: false
    });

    useEffect(() => {
        if (stateCmd.loadingCommands) {
            getCommands("");
        }
    }, [stateCmd]);

    useEffect(() => {
        if (stateAutoComplete.loading) {
            const takeRecords = autocompleteTakeRecords();

            props
                .autocomplete!.onGetSuggestions(stateAutoComplete.searchText, stateAutoComplete.searchSkipRecords, takeRecords + 1)
                .then((data) => {
                    var records = data.slice(0, takeRecords);
                    setStateAutoComplete({
                        ...stateAutoComplete,
                        loading: false,
                        autoCompleteItems:
                            stateAutoComplete.searchSkipRecords == 0 ? records : [...stateAutoComplete.autoCompleteItems, ...records],
                        searchSkipRecords: stateAutoComplete.searchSkipRecords + records.length,
                        suggestionLoadMore: data.length > records.length
                    });
                })
                .catch((error) => {
                    setStateAutoComplete({
                        ...stateAutoComplete,
                        loading: false,
                        suggestionLoadMore: false,
                        lastError: error.canceled ? null : error
                    });
                });
        }
    }, [stateAutoComplete]);

    //#region HANDLERS

    // Ao clicar em uma área fora do Dropdown.
    const handleOuterClick = (event: MouseEvent | TouchEvent) => {
        const target = event.target as HTMLElement;

        if (ref.contains(target)) {
            return;
        }

        setState({ ...state, commandOpened: false });

        if (props.onEscape) props.onEscape();
    };

    // Avisa a quem chamou, os novos valores do Texto de Busca e Filtros que devem ser aplicados.
    const updateSelection = (inputValue: string, selectedItems: AdvFilterResult[], pressedEnter: boolean = false) => {
        if (inputRef) inputRef.focus();
        if (props.onUpdateSelection) props.onUpdateSelection(inputValue, selectedItems, pressedEnter);
    };

    // Ao remover um Filtro.
    const handleDelete = (item: AdvFilterResult) => {
        const selectedItems = [...props.selectedCommands];
        const index = selectedItems.indexOf(item);
        selectedItems.splice(index, 1);
        setState({ ...state, commandOpened: false });
        updateSelection(state.inputValue, selectedItems);
    };

    // Controle do teclado no Input do Texto de Busca.
    const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        // IMPORTANTE: "nunca" trocar o evento para KeyDown, o que seria natural.
        // Devido o modo de operação do downshift é preciso que ocorra uma mudanção no INPUT para que o hightlight index seja resetado.

        const { inputValue, autoCompletedOpened } = state;

        if (event.keyCode == 27) {
            if (!props.autocomplete) {
                if (props.onEscape) props.onEscape();
                else if (++escapeCount > 1) {
                    escapeCount = 0;
                    setState({ ...state, inputValue: "" });
                }
            } else {
                // Em autocomplete, o ESC apenas reseta o input
                setState({ ...state, inputValue: "", autoCompletedOpened: false });
            }
        } else if (event.key == "/" && inputValue == "/" && props.commands != null && props.commands.length > 0 && !autoCompletedOpened) {
            // Entra em modo de Comando.

            setStateCmd({
                commandItems: [],
                loadingCommands: true
            });

            setState({
                ...state,
                commandOpened: true,
                inputCommand: ""
            });
        } else if (event.keyCode == 13) {
            if (props.onUpdateSelection) {
                props.onUpdateSelection(inputValue, props.selectedCommands, true);
            } else if (props.autocomplete) setState({ ...state, inputValue: "" });
        }
    };

    // Controle do estado do Input do Texto de Busca.
    const handleInputChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const v = (event.target as HTMLInputElement).value;

        // Entra modo de AutoComplete se o primeiro caracter digitado for '/'
        const autoCompletedOpened = props.autocomplete != null && v.length > 0 && v[0] != "/";

        setState({
            ...state,
            inputValue: v,
            autoCompletedOpened
        });
    };

    //#endregion

    //#region COMMAND

    // Verifica a ação que deve ser tomada ao utilizar o teclado.
    const handleKeyUpCmd = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, highlightedIndex: number | null) => {
        const { autocomplete } = props;

        if ((event.key == "/" && !autocomplete) || event.keyCode == 27 || event.keyCode == 13) {
            event.preventDefault();

            const { commandOpened } = state;
            let { inputValue } = state;

            if (event.keyCode == 13 && stateCmd.commandItems.length > 0 && commandOpened && highlightedIndex == null) {
                // Seleciona primeiro item da lista de comandos (match parcial do nome do comando).
                handleDownshiftChange(stateCmd.commandItems[0]);
                inputValue = "";
            } else if (event.keyCode == 27 || event.keyCode == 13) {
                inputValue = inputValue.substr(0, state.inputValue.length - 1); // Remove último '/'
            }

            setState({
                autoCompletedOpened: false,
                currentCommand: null,
                commandOpened: false,
                inputCommand: "",
                inputValue
            });
            inputRef.focus();
        }
    };

    // Atualiza estado do nome do comando
    const handleInputChangeCmd = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const v = (event.target as HTMLInputElement).value;
        setState({
            ...state,
            inputCommand: v
        });
    };

    // Adiciona um Comando ao Filtro.
    const internalAddCommand = (command: string, displayText: string | null, value: any) => {
        var selectedItems = advFilterAddCommand(props.commands, props.selectedCommands, command, value, displayText);

        let { inputValue } = state;
        inputValue = command == AutoCompleteKeyWord ? "" : inputValue.substr(0, inputValue.length - 1); // Remove último '/'

        setState({
            ...state,
            inputValue,
            autoCompletedOpened: false,
            commandOpened: false,
            currentCommand: null
        });

        updateSelection(inputValue, selectedItems);
    };

    // Obtém os comandos, filtrando por nome.
    const getCommands = (searchText: string | null) => {
        if (props.commands == null) setStateCmd({ commandItems: [], loadingCommands: false });
        else {
            const textToSearch = searchText || "";

            var cmds = props.commands.slice(0);

            props.selectedCommands.forEach((v) => {
                const c = props.commands!.find((p) => p.command == v.command);
                if (c && !c.canRepeat) {
                    cmds = cmds.filter((p) => p.command != v.command);
                }
                if (c && c.optionGroup) {
                    cmds = cmds.filter((p) => p.command == c.command || p.optionGroup != c.optionGroup);
                }
            });

            // O match nos commandos são CASE e ACCENT INSENSITIVE.
            var searchTextNorm = LibUtils.removeAccents(textToSearch).toLowerCase();

            var newCmds = cmds
                .filter((p) => textToSearch == "" || LibUtils.removeAccents(p.displayName)!.toLowerCase().indexOf(searchTextNorm) > -1)
                .sort((p) => p.group);

            setStateCmd({
                loadingCommands: false,
                commandItems: newCmds
            });
        }
    };

    // Renderiza os comandos em formato de CHIP.
    const commandChips = () => {
        let { selectedCommands } = props;
        if (props.autocomplete && !props.autocomplete.tokenizeSelection) {
            selectedCommands = selectedCommands.filter((p) => p.command != AutoCompleteKeyWord);
        }
        return selectedCommands.map((item) => (
            <MprcChip
                key={item.command}
                tabIndex={-1}
                label={item.text}
                size="small"
                className={classes.autocompleteChip}
                onDelete={() => handleDelete(item)}
            />
        ));
    };

    //#endregion

    //#region PICKERS

    const showPicker = () => {
        const { currentCommand } = state;

        switch (currentCommand?.picker) {
            case AdvFilterPicker.Custom:
                return (
                    currentCommand &&
                    currentCommand.customPicker &&
                    currentCommand.customPicker({
                        onSelect: handleCustomPicker
                    })
                );

            case AdvFilterPicker.GenericList:
                return (
                    currentCommand &&
                    currentCommand.genericListPicker &&
                    currentCommand.genericListPicker({
                        multi: false,
                        onSelect: handleSelectionListItem
                    })
                );

            case AdvFilterPicker.Calendar:
                return <DatePickerDialog onSelect={handleDatePicker} />;

            case AdvFilterPicker.Text:
                return <TextPickerDrawer label={currentCommand.displayName} onSelect={handleTextPicker} />;
        }
        return null;
    };

    const handleDatePicker = (dt: Date | null) => {
        if (dt != null && state.currentCommand != null) {
            internalAddCommand(state.currentCommand.command, Formaters.localeDate(dt), dt);
            setTimeout(updateInputRefPosition, 100);
        }
        afterPicker();
    };

    const handleTextPicker = (text: SearchTextDto | null) => {
        if (text != null && state.currentCommand) {
            var typeName = "";
            if (text.rule == SearchTextRule.Contains) typeName = theme.MprcAdvFilter.textPickerSearchTypes.contains;
            else if (text.rule == SearchTextRule.Equals) typeName = theme.MprcAdvFilter.textPickerSearchTypes.equals;
            else if (text.rule == SearchTextRule.StartWith) typeName = theme.MprcAdvFilter.textPickerSearchTypes.startWith;

            var displayText = typeName + ": " + Formaters.truncateString(text.text ?? "", 15);

            internalAddCommand(state.currentCommand.command, displayText, text);
            setTimeout(updateInputRefPosition, 100);
        }
        afterPicker();
    };

    const handleSelectionListItem = (v: GenericListItemDto[] | undefined) => {
        if (v != null && state.currentCommand) {
            internalAddCommand(state.currentCommand.command, v[0].name, v[0].id);
            setTimeout(updateInputRefPosition, 100);
        }
        afterPicker();
    };

    const handleCustomPicker = (result: CustomPickerResult | undefined) => {
        if (result && result.value != null && state.currentCommand) {
            internalAddCommand(state.currentCommand.command, result.text, result.value);
            setTimeout(updateInputRefPosition, 100);
        }
        afterPicker();
    };

    const afterPicker = () => {
        setState({ inputValue: "", inputCommand: "", currentCommand: null, autoCompletedOpened: false, commandOpened: false });
    };
    //#endregion

    //#region DownShift

    const updateInputRefPosition = () => {
        if (inputRef) inputRef.selectionStart = inputRef.selectionEnd = inputRef.length;
    };

    // Ao selecionar um item do menu de comando.
    const handleDownshiftChange = (cmd: AdvFilterCommand | AutoCompleteLegacyDataModel) => {
        if (cmd) {
            if ("command" in cmd) {
                const currentCmd = cmd as AdvFilterCommand;

                if (currentCmd.picker != null) {
                    setState({
                        ...state,
                        currentCommand: currentCmd,
                        commandOpened: false
                    });
                } else {
                    internalAddCommand(currentCmd.command, null, null);
                }
            } else {
                const c = cmd as AutoCompleteLegacyDataModel;
                internalAddCommand(AutoCompleteKeyWord, c.label, c);
            }
        }
    };

    // Ao mudar o estado de Downshift, carrega COMANDOS ou SUGESTÕES
    const handleDownshiftStateChange = (changes: StateChangeOptions<any>, downshiftState: ControllerStateAndHelpers<any>) => {
        if (downshiftState.isOpen) {
            if (state.autoCompletedOpened && changes.type == Downshift.stateChangeTypes.changeInput) {
                getSuggestions(downshiftState.inputValue ?? "", 0);
            }

            if (state.commandOpened && changes.type == Downshift.stateChangeTypes.changeInput) {
                getCommands(downshiftState.inputValue);
            }

            //if (state.commandOpened && changes.type == Downshift.stateChangeTypes.keyDownEnter) {
            //    internalAddCommand(state.commandItems[0].command, state.commandItems[0].displayName, null);
            //}
        }
    };

    // Obtém a quantidade de registros que devem ser carregados no Autocomplete.
    const autocompleteTakeRecords = () => {
        const { maxSuggestionCount } = props.autocomplete || {};
        return maxSuggestionCount != null ? maxSuggestionCount : 10;
    };

    // Obtém os itens do AutoComplete.
    const getSuggestions = (searchText: string, skipRecords: number) => {
        const { onGetSuggestions } = props.autocomplete || {};

        if (onGetSuggestions == undefined) return Promise.reject();

        setStateAutoComplete({
            ...stateAutoComplete,
            loading: true,
            lastError: null,
            searchText: searchText,
            searchSkipRecords: skipRecords
        });
    };

    //#endregion

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    let { placeholder, placeholderFocus, title } = props;
    if (placeholder == undefined) placeholder = theme.MprcAdvFilter.inputPlaceholder;
    if (placeholderFocus == undefined) placeholderFocus = theme.MprcAdvFilter.inputPlaceholderFocus;
    if (title == undefined) title = theme.MprcAdvFilter.inputTitleEnterToFilter;

    const { id, autoFocus = false, container, disabled, label, underlineColor = "muted" } = props;
    const { inputValue, inputCommand, commandOpened, autoCompletedOpened } = state;
    const { loadingCommands } = stateCmd;
    const { lastError, loading } = stateAutoComplete;
    const loadMoreId = id + "-loadmore";

    const iProps = {
        autoCapitalize: "off",
        autoComplete: "off",
        autoCorrect: "off",
        autoSave: "off",
        spellCheck: false
    };


    const popperClasses = cx({
        [classes.popperRoot]: true,
        [classes.popperRootOverModal]: props.overModal === true
    });

    return (
        <div id={id} className={classes.root} ref={(node) => { ref = node; }}>
            <Downshift
                id={id}
                inputValue={commandOpened ? inputCommand : inputValue}
                onChange={(e) => handleDownshiftChange(e)}
                onStateChange={handleDownshiftStateChange}
                selectedItem={null}
                isOpen={commandOpened || autoCompletedOpened}
            >
                {({
                    getInputProps,
                    getItemProps,
                    getMenuProps,
                    isOpen,
                    inputValue: inputValue2,
                    selectedItem: selectedItem2,
                    highlightedIndex,
                    setItemCount
                }) => {
                    // Ajusta ajustar a contagem de itens.
                    setItemCount(
                        commandOpened
                            ? stateCmd.commandItems.length
                            : stateAutoComplete.autoCompleteItems
                                ? stateAutoComplete.autoCompleteItems.length
                                : 0
                    );

                    return (
                        <div key={`adcontainer_${id}`} className={classes.container}>
                            {
                                !commandOpened
                                    ? <TextField
                                        fullWidth
                                        slotProps={{
                                            input: {
                                                ...getInputProps({
                                                    onChange: (e: React.FormEvent<HTMLTextAreaElement | HTMLInputElement>) => handleInputChange(e),
                                                    onKeyUp: (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => handleKeyUp(e),
                                                    onFocus: () => {
                                                        if (inputRef && placeholderFocus)
                                                            (inputRef as HTMLInputElement).placeholder = placeholderFocus;
                                                    },
                                                    onBlur: () => {
                                                        if (inputRef && placeholder) (inputRef as HTMLInputElement).placeholder = placeholder;

                                                        if (state.autoCompletedOpened)
                                                            setState({
                                                                ...state,
                                                                inputValue: "",
                                                                commandOpened: false,
                                                                autoCompletedOpened: false
                                                            });
                                                    },
                                                    placeholder: placeholder,
                                                    title: title,
                                                    autoFocus: autoFocus || false,
                                                    color: "warning",
                                                    ref: () => { }
                                                }),
                                                ...{
                                                    classes: {
                                                        root: classes.inputRoot,
                                                        input: classes.inputInput,
                                                        underline:
                                                            underlineColor ? colorClasses.under + " " + colorClasses["under_" + underlineColor as InputColorClassKeys] : undefined
                                                    },
                                                    startAdornment: commandChips(),
                                                    size: "small"
                                                },
                                                inputProps: iProps
                                            }
                                        }}
                                        disabled={disabled || false}
                                        inputRef={(node) => {
                                            inputRef = node;
                                        }}
                                        label={label}
                                    />
                                    : <TextField
                                        fullWidth
                                        slotProps={{
                                            input: {
                                                ...getInputProps({
                                                    onChange: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => handleInputChangeCmd(e),
                                                    onKeyUp: (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => handleKeyUpCmd(e, highlightedIndex),
                                                    style: { backgroundColor: "#c0c0c0" },
                                                    color: "primary",
                                                    ref: () => { }
                                                }),
                                                ...{
                                                    classes: {
                                                        root: classes.inputRoot,
                                                        input: classes.inputInput,
                                                        underline:
                                                            underlineColor == "inherit"
                                                                ? classes.inputUnderline
                                                                : underlineColor == "white"
                                                                    ? classes.inputUnderlineWhite
                                                                    : underlineColor == "black"
                                                                        ? classes.inputUnderlineBlack
                                                                        : classes.inputUnderlineMuted
                                                    },
                                                    startAdornment: commandChips(),
                                                    size: "small"
                                                },
                                                inputProps: iProps
                                            }
                                        }}
                                        inputRef={(node) => {
                                            inputRef = node;
                                        }}
                                    />
                            }
                            <div ref={popperRef}>
                                <div {...getMenuProps()}>
                                    {
                                        autoCompletedOpened &&
                                        <Popper
                                            disablePortal={false}
                                            open={true}
                                            anchorEl={popperRef.current}
                                            className={popperClasses}
                                            container={container ? document.getElementById(container) : document.body}
                                        >
                                            <Paper
                                                square={true}
                                                style={
                                                    {
                                                        width: popperRef.current ? popperRef.current.clientWidth : undefined,
                                                        fontSize: "0.75rem"
                                                    }
                                                }
                                            >
                                                {
                                                    lastError
                                                        ? <MprcRequestErrorReport error={lastError} />
                                                        : loading
                                                            ? <MprcSpinnerIndicator />
                                                            : stateAutoComplete.autoCompleteItems.length == 0
                                                                ? <EmptyRecordSet />
                                                                : stateAutoComplete.autoCompleteItems.map((suggestion, index) => (
                                                                    <RenderSuggestion
                                                                        key={suggestion.key}
                                                                        suggestion={suggestion}
                                                                        index={index}
                                                                        currentInputValue={inputValue}
                                                                        itemProps={getItemProps({ item: suggestion })}
                                                                        highlightedIndex={suggestion.label == inputValue ? index : highlightedIndex}
                                                                        selectedItem={selectedItem2 != null ? selectedItem2.label : null}
                                                                    />
                                                                ))
                                                }

                                                {
                                                    stateAutoComplete.suggestionLoadMore
                                                        ? (
                                                            <>
                                                                <Divider />
                                                                <MenuItem
                                                                    {...getItemProps({ item: loadMoreId })}
                                                                    key={loadMoreId}
                                                                    className={classes.loadMore}
                                                                    onClick={() => {
                                                                        getSuggestions(state.inputValue, stateAutoComplete.searchSkipRecords);
                                                                    }}
                                                                    component="div"
                                                                >
                                                                    <LoadMoreText count={autocompleteTakeRecords()} />
                                                                </MenuItem>
                                                            </>
                                                        )
                                                        : null
                                                }
                                            </Paper>
                                        </Popper>
                                    }

                                    {
                                        commandOpened &&
                                        <ClickAwayListener onClickAway={(e) => handleOuterClick(e)}>
                                            <Popper
                                                disablePortal={false}
                                                open={true}
                                                anchorEl={popperRef.current}
                                                className={popperClasses}
                                                container={container ? document.getElementById(container) : document.body}
                                            >
                                                <Paper
                                                    square={true}
                                                    style={{
                                                        width: popperRef.current ? popperRef.current.clientWidth : undefined,
                                                        fontSize: "0.75rem"
                                                    }}
                                                >
                                                    {
                                                        loadingCommands
                                                            ? <MprcSpinnerIndicator />
                                                            : stateCmd.commandItems.length == 0
                                                                ? (
                                                                    <MenuItem>
                                                                        <span>Não há comandos</span>
                                                                    </MenuItem>
                                                                )
                                                                : (
                                                                    stateCmd.commandItems.map((v, index, arr) => (
                                                                        <Fragment key={v.command}>
                                                                            {
                                                                                index > 0 && arr[index].group != arr[index - 1].group &&
                                                                                <Divider />
                                                                            }
                                                                            <MenuItem
                                                                                {...getItemProps({ item: v })}
                                                                                selected={highlightedIndex == index}
                                                                                component="div"
                                                                                className={classes.popList}
                                                                            >
                                                                                <span
                                                                                    dangerouslySetInnerHTML={{
                                                                                        __html: Formaters.htmlHighlightTerm(inputCommand, v.displayName)
                                                                                    }}
                                                                                ></span>
                                                                            </MenuItem>
                                                                        </Fragment>
                                                                    ))
                                                                )
                                                    }
                                                </Paper>
                                            </Popper>
                                        </ClickAwayListener>
                                    }
                                </div>
                            </div>
                        </div>
                    );
                }}
            </Downshift>

            {state.currentCommand?.picker != null && showPicker()}
        </div>
    );
};

export const MprcAdvFilter = React.forwardRef(MprcAdvFilterComponent);
